import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { ImportKnowledge } from './ImportKnowledge';
import {
  addKnowledge,
  editKnowledge,
  getKnowledge,
  knowledgeTone,
  listKnowledge,
  reindexKnowledge,
  removeKnowledge,
  searchKnowledge,
  type KnowledgeHit,
  type KnowledgeSource,
} from './api';
import { useRealtimeTopic } from '../app/realtime-store';

/**
 * The knowledge base (context.md sections 93-96).
 *
 * Two halves, and the second is the one that makes the first trustworthy.
 *
 * On the left, the documents and whether each is actually searchable. `PENDING` and `FAILED` are
 * shown plainly because a document a tenant believes is indexed and is not is the single worst state
 * here: the agent answers "não encontrei" about something sitting right there, and nobody knows why.
 *
 * On the right, a search box that runs exactly the query the agent's tool runs. Somebody can ask
 * "would the agent find this?" before finding out from a customer, and see the similarity score that
 * decided it. A retrieval system nobody can interrogate is one nobody can fix.
 */
export function KnowledgePage(): React.JSX.Element {
  const client = useQueryClient();

  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sources = useQuery({
    queryKey: ['knowledge'],
    queryFn: () => listKnowledge(),
    /*
     * Pushed, with a slow floor.
     *
     * The indexer says when a document is ready (Phase 80), so the five-second poll is now a
     * safety net rather than the mechanism: a socket that is silently broken looks exactly like an
     * index that is taking its time.
     */
    refetchInterval: (query) =>
      (query.state.data?.sources ?? []).some(
        (source) => source.status === 'PENDING' || source.status === 'INDEXING',
      )
        ? 30_000
        : false,
  });

  useRealtimeTopic('knowledge', () => {
    void client.invalidateQueries({ queryKey: ['knowledge'] });
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['knowledge'] });
  };

  const reindex = useMutation({
    mutationFn: (id: string) => reindexKnowledge(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeKnowledge(id),
    onSuccess: () => {
      setError(null);
      setOpen(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const rows = sources.data?.sources ?? [];
  const notReady = rows.filter((source) => source.status !== 'READY').length;
  const selected = rows.find((source) => source.id === open) ?? null;
  const busy = reindex.isPending || remove.isPending;

  const columns: Column<KnowledgeSource>[] = [
    {
      key: 'document',
      header: t('ai.document'),
      text: (source) => `${source.title} ${source.tags.join(' ')}`.trim(),
      render: (source) => (
        <span className="cell-stack">
          <span className="strong">{source.title}</span>
          <span className="muted small">
            {t('ai.characters').replace('{n}', formatNumber(source.characters))}
            {source.tags.length > 0 && ` · ${source.tags.join(', ')}`}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (source) => `${t(`ai.status.${source.status}`)} ${source.failureReason ?? ''}`.trim(),
      render: (source) => (
        <span className="cell-stack">
          <Badge tone={knowledgeTone(source.status)}>{t(`ai.status.${source.status}`)}</Badge>
          {source.failureReason && <span className="muted small">{source.failureReason}</span>}
        </span>
      ),
    },
    {
      key: 'chunkCount',
      header: t('ai.chunks'),
      align: 'end',
      render: (source) => source.chunkCount,
    },
    {
      key: 'indexedAt',
      header: t('ai.indexedAt'),
      text: (source) => (source.indexedAt ? formatDateTime(source.indexedAt) : ''),
      render: (source) => (
        <span className="muted small">
          {source.indexedAt ? formatDateTime(source.indexedAt) : '—'}
        </span>
      ),
    },
    // Off by default; the chooser turns them on and the account remembers the choice.
    {
      key: 'characters',
      header: t('ai.charactersLabel'),
      hidden: true,
      align: 'end',
      text: (source) => formatNumber(source.characters),
      render: (source) => formatNumber(source.characters),
    },
    {
      key: 'tags',
      header: t('files.tags'),
      hidden: true,
      text: (source) => source.tags.join(', '),
      render: (source) =>
        source.tags.length > 0 ? source.tags.join(', ') : <span className="muted">—</span>,
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      hidden: true,
      text: (source) => formatDateTime(source.createdAt),
      render: (source) => formatDateTime(source.createdAt),
    },
    {
      key: 'updatedAt',
      header: t('record.field.updated_at'),
      hidden: true,
      text: (source) => formatDateTime(source.updatedAt),
      render: (source) => formatDateTime(source.updatedAt),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (source) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={busy}
            onClick={(event) => {
              // The row opens the editor; neither button should also do that.
              event.stopPropagation();
              reindex.mutate(source.id);
            }}
          >
            {t('ai.reindex')}
          </button>
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              remove.mutate(source.id);
            }}
          >
            {t('confirm.remove')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('ai.knowledge')}
        description={t('ai.knowledgeDescription')}
        actions={
          <span className="cell-inline">
            {/* Bringing in what the tenant already wrote, before offering a blank page. */}
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => {
                setImporting((current) => !current);
                setAdding(false);
                setOpen(null);
              }}
            >
              {importing ? t('confirm.cancel') : t('ai.import.open')}
            </button>

            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => {
                setAdding((current) => !current);
                setImporting(false);
                setOpen(null);
              }}
            >
              {adding ? t('confirm.cancel') : t('ai.addDocument')}
            </button>
          </span>
        }
      />

      {importing && <ImportKnowledge onDone={() => setImporting(false)} />}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {adding && (
        <Editor
          onDone={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}

      <div className="ticket">
        <div className="ticket__thread">
          <Panel
            title={t('ai.documents')}
            actions={
              notReady > 0 ? (
                <span className="muted small">
                  {t('ai.notReady').replace('{n}', String(notReady))}
                </span>
              ) : undefined
            }
          >
            <DataTable
              tableKey="ai-knowledge"
              rows={sources.data?.sources}
              columns={columns}
              rowKey={(source) => source.id}
              isLoading={sources.isLoading}
              error={sources.error}
              onRetry={() => void sources.refetch()}
              onRowClick={(source) =>
                setOpen((current) => (current === source.id ? null : source.id))
              }
              empty={{ title: t('ai.noDocuments') }}
            />
          </Panel>

          {/*
           * The editor below the table rather than inside it.
           *
           * The list is paged and filtered now, so a document edited in an expanded row would slide
           * away under whoever is typing the moment either changes. A panel of its own stays put.
           */}
          {selected && (
            <Panel title={selected.title}>
              <Editor sourceId={selected.id} onDone={refresh} />
            </Panel>
          )}
        </div>

        <aside className="ticket__side">
          <TryIt />
        </aside>
      </div>
    </div>
  );
}

/**
 * Writing or editing a document.
 *
 * A plain textarea with markdown headings, because that is what the chunker reads: `##` starts a
 * section and the heading travels with every chunk beneath it. Saying so under the box is worth more
 * than a rich editor here — somebody who knows their headings become the searchable labels writes
 * better documents, and no toolbar teaches that.
 *
 * Split in two so the form holds no effect. The wrapper waits for the document and then mounts the
 * form **keyed by its id**, which is React's own answer to "reset this state when the subject
 * changes". Seeding state from a prop in an effect is the version of this that overwrites what
 * somebody is typing the moment anything triggers a refetch.
 */
function Editor({
  sourceId,
  onDone,
}: {
  sourceId?: string;
  onDone: () => void;
}): React.JSX.Element {
  const existing = useQuery({
    queryKey: ['knowledge', sourceId],
    queryFn: () => getKnowledge(sourceId!),
    enabled: sourceId !== undefined,
  });

  if (sourceId === undefined) return <EditorForm key="new" onDone={onDone} />;
  if (existing.isLoading) return <Loading />;
  if (existing.error) {
    return <ErrorState error={existing.error} onRetry={() => void existing.refetch()} />;
  }
  if (!existing.data) return <Loading />;

  return (
    <EditorForm
      key={existing.data.id}
      sourceId={existing.data.id}
      initialTitle={existing.data.title}
      initialContent={existing.data.content}
      onDone={onDone}
    />
  );
}

function EditorForm({
  sourceId,
  initialTitle = '',
  initialContent = '',
  onDone,
}: {
  sourceId?: string;
  initialTitle?: string;
  initialContent?: string;
  onDone: () => void;
}): React.JSX.Element {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      sourceId
        ? editKnowledge(sourceId, { title: title.trim(), content: content.trim() })
        : addKnowledge({ title: title.trim(), content: content.trim() }),
    onSuccess: () => {
      setError(null);

      if (!sourceId) {
        setTitle('');
        setContent('');
      }

      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="stack">
      <label className="stack__field">
        <span>
          {t('ai.documentTitle')} <span className="required">*</span>
        </span>
        <input value={title} maxLength={300} onChange={(event) => setTitle(event.target.value)} />
      </label>

      <label className="stack__field">
        <span>{t('ai.documentContent')}</span>
        <textarea
          rows={14}
          value={content}
          placeholder={t('ai.documentPlaceholder')}
          onChange={(event) => setContent(event.target.value)}
        />
        <span className="muted small">{t('ai.documentHint')}</span>
      </label>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      <div className="confirm__actions">
        <button
          type="button"
          className="button button--primary button--compact"
          disabled={title.trim() === '' || content.trim() === '' || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('work.creating') : t('ai.saveDocument')}
        </button>
      </div>
    </div>
  );
}

/** The agent's own search, run by hand. */
function TryIt(): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<KnowledgeHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => searchKnowledge(query.trim()),
    onSuccess: (result) => {
      setError(null);
      setHits(result.hits);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Panel title={t('ai.tryIt')}>
      <p className="muted small">{t('ai.tryItHint')}</p>

      <form
        className="stack__field"
        onSubmit={(event) => {
          event.preventDefault();
          if (query.trim()) run.mutate();
        }}
      >
        <input
          type="search"
          value={query}
          placeholder={t('ai.tryItPlaceholder')}
          maxLength={500}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button
          type="submit"
          className="button button--compact"
          disabled={query.trim() === '' || run.isPending}
        >
          {run.isPending ? t('ai.searching') : t('crm.search')}
        </button>
      </form>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      {hits !== null &&
        (hits.length === 0 ? (
          /*
           * Nothing found is a real answer and is shown as one.
           *
           * It is also what the agent would have been handed, which is the point: the alternative —
           * returning the nearest chunks regardless of score — is how a bot invents a refund policy.
           */
          <p className="muted">{t('ai.noHits')}</p>
        ) : (
          <ul className="hits">
            {hits.map((hit) => (
              <li key={hit.chunkId} className="hit">
                <div className="hit__head">
                  <span className="strong small">{hit.heading ?? hit.title}</span>
                  <span className="muted small">{Math.round(hit.similarity * 100)}%</span>
                </div>
                <p className="hit__body">{hit.content}</p>
              </li>
            ))}
          </ul>
        ))}
    </Panel>
  );
}
